import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Role } from './useRoles';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

export interface TeamWithRoles {
  teamId: string;
  teamName: string;
  organizationId: string;
  divisionId?: string;
  divisionName?: string;
  roles: Role[];
}

export interface ContextTeamsResponse {
  contextId: string;
  teams: TeamWithRoles[];
}

interface AssignTeamRoleRequest {
  teamId: string;
  roleId: string;
}

interface TeamRoleAssignmentResponse {
  contextId: string;
  teamId: string;
  roleId: string;
  message: string;
}

const backendApi = config.backendApi;

/**
 * Formats a team name with its division distinguisher for TeamWithRoles.
 * - If the team has a division, appends " (DivisionName)"
 * - If the team is org-level (no division), appends " (org)"
 */
export function formatTeamWithRolesName(teamWithRoles: TeamWithRoles): string {
  if (teamWithRoles.divisionName) {
    return `${teamWithRoles.teamName} (${teamWithRoles.divisionName})`;
  }
  return `${teamWithRoles.teamName} (org)`;
}

const fetchContextTeams = async (contextId: string): Promise<TeamWithRoles[]> => {
  const response = await axios.get<ContextTeamsResponse>(
    `${backendApi}/api/contexts/${contextId}/teams`,
    { withCredentials: true }
  );
  return response.data.teams;
};

const assignTeamRole = async (contextId: string, data: AssignTeamRoleRequest): Promise<TeamRoleAssignmentResponse> => {
  const response = await axios.post<TeamRoleAssignmentResponse>(
    `${backendApi}/api/contexts/${contextId}/teams`,
    data,
    { withCredentials: true }
  );
  return response.data;
};

const removeTeamRole = async (contextId: string, teamId: string, roleId: string): Promise<void> => {
  await axios.delete(
    `${backendApi}/api/contexts/${contextId}/teams/${teamId}/roles/${roleId}`,
    { withCredentials: true }
  );
};

// Hook to fetch teams and their roles for a specific context
export function useContextTeams(contextId: string | null | undefined) {
  const contextReady = useActiveContextReady();

  return useQuery<TeamWithRoles[]>({
    queryKey: ['contexts', contextId, 'teams'],
    queryFn: () => {
      if (!contextId) {
        throw new Error("contextId is required to fetch context teams.");
      }
      return fetchContextTeams(contextId);
    },
    enabled: !!contextId && contextReady,
  });
}

// Hook to assign a role to a team in a context
export function useAssignTeamRole(contextId: string) {
  const queryClient = useQueryClient();

  return useMutation<TeamRoleAssignmentResponse, Error, AssignTeamRoleRequest>({
    mutationFn: (data) => assignTeamRole(contextId, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contexts', contextId, 'teams'] });
    },
  });
}

// Hook to remove a role from a team in a context
export function useRemoveTeamRole(contextId: string) {
  const queryClient = useQueryClient();

  return useMutation<undefined, Error, { teamId: string; roleId: string }>({
    mutationFn: async ({ teamId, roleId }) => {
      await removeTeamRole(contextId, teamId, roleId);
      return undefined;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['contexts', contextId, 'teams'] });
    },
  });
}
